/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import detectIt from 'detect-it';
import { useRouter } from 'next/router';
import { isCompletedWithNoVideoUrl, isPostClickable } from '@models/Item';
import Dynamic from '@base/Dynamic/Dynamic';
import { enrollInBatch, fetchSimilarBatches } from '@actions/batches';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import { useSnackbar } from '@stores/snackbar';
import {
  BATCH_VIEWED,
  BATCH_KEYS,
  BATCH_UNENROLLED,
  BATCH_ENROLLED
} from '@constants/segment';
import { evtNameCategoryMap, getCourseEnrollMessage } from '@constants/index';
import isEmptyObject from '@utils/isEmptyObject';
import { wordsToSentence } from '@utils/textHelper';
import batchSchema from '@seo/batch/batch';
import SEO from '@seo/index';
import useBatchDetails from '@api/hooks/batch/useBatchDetails';
import useBatchSchedule from '@api/hooks/batch/useBatchSchedule';
import useBatchTopicsCovered from '@api/hooks/batch/useBatchTopicsCovered';
import useBatchTestSeries from '@api/hooks/batch/useBatchTestSeries';
import useBatchTrialClasses from '@api/hooks/batch/useBatchTrialClasses';
import useBatchFeedback from '@api/hooks/batch/useBatchFeedback';
import { getIsLoggedIn } from '@api/selectors/auth';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useBatchSkuDetails from '@api/hooks/batch/useBatchSkuDetails';
import {
  getIsSubscribedToBatch,
  getIsSubscribedToPlus
} from '@selectors/subscription';
import { getBatchDemoClassesKey } from '@api/hooks/batch/useDemoClasses';
import useStorePlaylistFeed from '@api/hooks/batch/useStorePlaylistFeed';
import useStorePlaylistTestFeed from '@api/hooks/batch/useStorePlaylistTestFeed';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { REFER_LINK_CLICKED } from '@cont/Checkout/constants/segment';
import { emptyObject } from '@constants/empty';
import useIsBatchSubscribedStore from '@stores/batchIsSubscribedStore';
import styled from '@emotion/styled';
import { Spacings, mediaQuery } from '@styles/index';
import {
  BATCH_CONTENT_TYPE_ID,
  BATCH_CONTENT_TYPE_MAP,
  BATCH_SIGNUP_BANNER,
  EDUCATOR_TYPES,
  getAccessEndDate,
  getIsPartnerEducatorBatch,
  getIsStorePath,
  getIsStoreSyllabus,
  setBatchReferralInStorage
} from './utils';
import BatchHeader from './BatchHeader';
import BatchDemoClasses from './DemoClasses/DemoClasses';
import BatchAbout from './BatchAbout';

const Dialog = Dynamic(() => import('@comp/Dialog/Dialog'));
const Menu = Dynamic(() => import('@base/Menu/Menu'));
const PDFDownload = Dynamic(() => import('../Course/PDFDownload'));
// const BatchDemoClasses = Dynamic(() => import('./DemoClasses/DemoClasses'));
const TestSeries = Dynamic(() => import('./TestSeries'));
const ContinueWatching = Dynamic(() =>
  import('./RecordedBatch/ContinueWatching')
);
const UpcomingBatch = Dynamic(() => import('./RecordedBatch/UpcomingBatch'));
const RecordedSchedule = Dynamic(() =>
  import('./RecordedBatch/RecordedSchedule')
);
const Week = Dynamic(() => import('../Course/Week'));
const SyllabusTest = Dynamic(() => import('./StoreSyllabus/SyllabusTest'));
const TrialClasses = Dynamic(() => import('./TrialClasses'));
const BatchEducators = Dynamic(() => import('./BatchEducators'));
const TopicsCovered = Dynamic(() => import('./TopicsCovered'));
const BatchFeedbacks = Dynamic(() => import('./BatchFeedbacks'));
const BatchFaqs = Dynamic(() => import('./BatchFaqs'));
const SimilarBatches = Dynamic(() => import('./SimilarBatches'));
const SignupGenericBanner = Dynamic(() =>
  import('@comp/SignupGenericBanner/SignupGenericBanner')
);

const emptyEndDate = 'N/A';

const LoginBannerContainer = styled.div`
  display: flex;
  grid-column: span 24;
`;

const StyledSignupBanner = styled(SignupGenericBanner)`
  margin-bottom: ${Spacings.SPACING_10B};
  width: 100%;
  ${mediaQuery.aboveMobile} {
    height: ${Spacings.SPACING_50B};
  }
`;

const Batch = ({ batchUID, faqs, freeTrialData }) => {
  const dispatch = useDispatch();
  const {
    batchDetails,
    mutate: refetchBatchData,
    isLoading: isBatchDetailsLoading
  } = useBatchDetails(batchUID);
  const { schedule, mutate: refetchScheduleData } = useBatchSchedule(batchUID);
  const { topicsCovered = [], mutate: refetchTopicsCovered } =
    useBatchTopicsCovered(batchUID);
  const { testSeries = [] } = useBatchTestSeries(batchUID);
  const { trialClasses = [], mutate: refetchTrialClasses } =
    useBatchTrialClasses(batchUID);

  const {
    goal,
    name,
    languages,
    thumbnail,
    slug: batchSlug,
    authors,
    isSellable,
    isEnrolled: isEnrolledInBatch,
    contentType,
    additionalFeatures
  } = batchDetails || {};
  const { storePlaylistFeed } = useStorePlaylistFeed(batchUID);
  const { storePlaylistTestFeed } = useStorePlaylistTestFeed({ batchUID });
  const { setIsBatchSubscribed, isBatchSubscribed } =
    useIsBatchSubscribedStore();

  const { batchFeedback, mutate: refetchBatchFeedback } = useBatchFeedback(
    goal?.uid
  );
  const isRecordedOrHybrid =
    contentType === BATCH_CONTENT_TYPE_MAP.LIVE_RECORDED ||
    contentType === BATCH_CONTENT_TYPE_MAP.RECORDED;
  const { batchSkuDetails, isLoading: isBatchSkuDetailsLoading } =
    useBatchSkuDetails(goal?.uid, batchUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  useEffect(() => {
    const isSubscribedToBatch = getIsSubscribedToBatch(
      batchDetails,
      batchSkuDetails
    );
    setIsBatchSubscribed(isSubscribedToBatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !!batchDetails,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !!batchSkuDetails,
    isLoggedIn,
    batchSkuDetails?.metaInfo
  ]);

  const selectedGoalUID = useSelectedGoalUID();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [unEnrollModalOpen, setUnEnrollModalOpen] = useState(false);
  const [scroll, setScroll] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const isSubscribed = useSelector(getIsSubscribedToPlus(goal?.uid));

  const { open: showSnackbar } = useSnackbar();
  const router = useRouter();
  const { route, query, isReady } = router || {};
  const isStoreRoute = getIsStorePath(route);

  const isStoreSyllabus = getIsStoreSyllabus({ additionalFeatures });

  const setPDF = (elem, lesson) => {
    setAnchorEl(elem);
    setSelectedLesson(lesson);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const batchRef = useRef('');
  const hasRouteUpdated = useRef(false);
  const educatorSectionRef = useRef(null);
  const referralCode = query.referral_code;

  useEffect(() => {
    if (batchUID) {
      dispatch(fetchSimilarBatches(batchUID));

      if (referralCode) {
        setBatchReferralInStorage(batchUID, referralCode);
      }
    }
  }, [dispatch, batchUID, referralCode]);

  useEffect(() => {
    if (
      !hasRouteUpdated.current &&
      isReady &&
      batchSlug &&
      query?.slug !== batchSlug
    ) {
      hasRouteUpdated.current = true;
      const queryParams = { ...query };
      delete queryParams.slug;
      delete queryParams.batchUID;
      const queryParamsString = new URLSearchParams(queryParams).toString();
      const queryParamsURL = queryParamsString ? `?${queryParamsString}` : '';
      if (isStoreRoute) {
        router.replace(
          `/store/batch/[slug]/[batchUID]/${queryParamsURL}`,
          `/store/batch/${batchSlug}/${batchUID}/${queryParamsURL}`,
          { shallow: true }
        );
      } else {
        router.replace(
          `/batch/[slug]/[batchUID]/${queryParamsURL}`,
          `/batch/${batchSlug}/${batchUID}/${queryParamsURL}`,
          { shallow: true }
        );
      }
    }
  }, [query?.slug, batchUID, batchSlug, router, isStoreRoute, isReady, query]);

  const sendRefferalClickEvent = (eventData) => {
    if (eventData.referrer_code) {
      segment.track(REFER_LINK_CLICKED, {
        ...eventData,
        product_type: eventData.product_type || 'Subscription',
        last_primary_source: 'Deeplink'
      });
    }
  };

  useEffect(() => {
    if (isReady) sendRefferalClickEvent(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  useEffect(() => {
    if (isLoggedIn) {
      refetchBatchData();
      refetchScheduleData();
      refetchTrialClasses();
      refetchBatchFeedback();
      refetchTopicsCovered();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // dummy change 2

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop } = document.documentElement;
      if (scrollTop > 300 && !scroll) setScroll(true);
      else if (scrollTop < 300 && scroll) setScroll(false);
    };
    window.addEventListener(
      'scroll',
      handleScroll,
      detectIt.passiveEvents === true
        ? { capture: false, passive: true }
        : false
    );
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scroll]);
  const goalUID = batchDetails?.goal?.uid;
  const isPartnerEducatorBatch = getIsPartnerEducatorBatch(batchDetails);
  const goalSegmentData = useGoalEventProps({ goalUID });
  const segmentData = useAllTypesOfCourseEventProps({
    course: batchDetails,
    isBatch: true
  });
  useEffect(() => {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    if (params.get('from_live_class') === 'true') return;
    const hasTrialClasses = isEmptyObject(batchDetails?.trialClasses ?? {});
    const lps = segmentStorage.getItem('lps') || 'Goal Home';
    const lpss = segmentStorage.getItem('lpss') || 'Non Hero Batch Section';
    if (!isBatchDetailsLoading && !isBatchSkuDetailsLoading) {
      segment.track(
        BATCH_VIEWED,
        {
          ...goalSegmentData,
          ...segmentData,
          batch_state: batchDetails?.state,
          has_trial_classes: hasTrialClasses,
          is_purchased: !!isPurchased,
          is_sellable: !!isSellable,
          is_store_exclusive: batchDetails?.isStoreExclusive,
          educator_type:
            batchDetails?.educatorType === EDUCATOR_TYPES.PARTNER
              ? 'Partner'
              : 'Plus',
          ...(lpss && { last_primary_source_section: lpss }),
          ...(lps && { last_primary_source: lps }),
          ...(isPurchased && { is_subscription_active: true }),
          ...(isPurchased && { user_subscription_plan: 'Store' })
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        BATCH_KEYS
      );
      if (lpss) segmentStorage.deleteItem('lpss');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPurchased,
    isSellable,
    isBatchDetailsLoading,
    isBatchSkuDetailsLoading
  ]);

  const toggleEnroll = useCallback(
    (batchData) => async (batchUid, isEnrolled) => {
      if (isEnrolled) {
        batchRef.current = batchData;
        setUnEnrollModalOpen(true);
      } else {
        segment.track(
          BATCH_ENROLLED,
          {
            is_purchased: !!isPurchased,
            is_sellable: !!isSellable,
            is_store_exclusive: batchDetails?.isStoreExclusive,
            educator_type:
              batchDetails?.educatorType === EDUCATOR_TYPES.PARTNER
                ? 'Partner'
                : 'Plus',
            ...goalSegmentData,
            ...segmentData
          },
          evtNameCategoryMap.WITHOUT_CATEGORY,
          true,
          BATCH_KEYS
        );
        await dispatch(enrollInBatch(batchUid, !isEnrolled));
        const { name: goalName } = batchData?.goal || {};
        showSnackbar({
          message: getCourseEnrollMessage({
            type: 'Courses',
            goalUID,
            selectedGoalUID,
            goalName
          }),
          type: 'success'
        });
        refetchBatchData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, batchDetails, isPurchased, isSellable]
  );

  const acceptOnClick = async () => {
    segment.track(
      BATCH_UNENROLLED,
      {
        ...goalSegmentData,
        ...segmentData,
        batch_state: batchDetails.state,
        start_date: batchDetails.startsAt,
        end_date: batchDetails.completedAt,
        is_purchased: !!isPurchased,
        is_sellable: !!isSellable,
        is_store_exclusive: batchDetails?.isStoreExclusive,
        educator_type:
          batchDetails?.educatorType === EDUCATOR_TYPES.PARTNER
            ? 'Partner'
            : 'Plus'
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      BATCH_KEYS
    );
    await dispatch(enrollInBatch(batchRef.current.uid, false));
    refetchBatchData();
    setUnEnrollModalOpen(false);
  };

  const onCloseModal = () => setUnEnrollModalOpen(false);

  const isPlusLessonClickable = (lesson) => {
    if (isCompletedWithNoVideoUrl(lesson) && !isPurchased) return false;
    return isPostClickable(lesson);
  };

  const onItemClick = (lesson) => {
    const { type, slug, uid } = lesson;
    if (type === 'post' && isPlusLessonClickable(lesson)) {
      window.open(`/class/${slug}/${uid}`, '_blank');
    } else if (type === 'quiz') {
      window.open(`/quiz/${slug}/${uid}`, '_blank');
    }
  };

  const goalName = goal?.name || '';
  const metaTitle = `${goalName || 'Goal'} - ${name || 'Batch'} Offered by
  Unacademy`;
  const metaDescription = `${goalName || 'Goal'} - ${name || 'Batch'} is a
  course in ${wordsToSentence(
    languages,
    'label',
    ', ',
    ', '
  )} delivered by Top Educators. Join Unacademy to access the course.`;
  const canonicalURL = batchSlug
    ? `https://unacademy.com/batch/${batchSlug}/${batchUID}`
    : null;

  const accessEndDate = getAccessEndDate(
    batchSkuDetails,
    batchUID,
    isStoreRoute,
    isSubscribed
  );
  const { skus = [] } = batchSkuDetails ?? emptyObject;
  const { displayDuration = '', duration = '' } = skus[0] ?? emptyObject;

  let metaDuration = '';
  let purchasedAccessEndDate = '';
  const batchSKuDetailsMetaInfo = batchSkuDetails?.metaInfo;
  if (!skus.length) {
    const subscription =
      !isEmptyObject(batchSKuDetailsMetaInfo) &&
      batchSKuDetailsMetaInfo?.latestUserSkusTypeWise[BATCH_CONTENT_TYPE_ID]
        ? batchSKuDetailsMetaInfo?.latestUserSkusTypeWise[
            BATCH_CONTENT_TYPE_ID
          ][0]?.subscription
        : null;

    if (subscription) {
      const { duration: upDuration, accessEndDate: upAccessEndDate } =
        subscription;
      metaDuration = upDuration;
      purchasedAccessEndDate = upAccessEndDate;
    }
  }

  const finalDuration = duration || metaDuration;

  const isDurationBased = isEnrolledInBatch
    ? !purchasedAccessEndDate && !!finalDuration
    : accessEndDate === emptyEndDate && !!finalDuration;

  return (
    <>
      <SEO
        title={metaTitle}
        url={canonicalURL}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: thumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={batchSchema({
          batch: { name, permalink: canonicalURL },
          batchDetails
        })}
      />
      <Dialog
        onClose={onCloseModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message="All courses from this batch will be removed from your schedule and you will stop receiving updates related to this batch"
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <Menu anchorEl={anchorEl} onClose={handleClose} position="right">
        <PDFDownload
          selectedLesson={selectedLesson}
          handleClose={handleClose}
          isBatch
        />
      </Menu>
      <BatchHeader
        batchUID={batchUID}
        scroll={scroll}
        toggleEnroll={toggleEnroll(batchDetails)}
        educatorSectionRef={educatorSectionRef}
        handlePurchase={setIsPurchased}
        isRecordedOrHybrid={isRecordedOrHybrid}
        freeTrialData={freeTrialData}
        isDurationBased={isDurationBased}
      />
      <LoginBannerContainer>
        <StyledSignupBanner
          data={BATCH_SIGNUP_BANNER}
          goalUID={selectedGoalUID}
          bannerV2
        />
      </LoginBannerContainer>
      {isStoreRoute ? (
        ''
      ) : (
        <BatchDemoClasses
          apiKey={getBatchDemoClassesKey(batchUID)}
          goalUID={goalUID}
        />
      )}
      <BatchAbout
        batch={batchDetails}
        batchUID={batchUID}
        isPurchased={isPurchased}
        isRecordedOrHybrid={isRecordedOrHybrid}
        accessEndDate={accessEndDate}
        isDurationBased={isDurationBased}
        displayDuration={displayDuration}
      />
      <TestSeries
        testSeries={testSeries}
        isEnrolledInBatch={isEnrolledInBatch}
      />
      {isEnrolledInBatch && isRecordedOrHybrid && isPurchased ? (
        <>
          <ContinueWatching
            data={batchDetails.continueWatching}
            onItemClick={onItemClick}
            batchGoalUID={goalUID}
          />
          <UpcomingBatch
            batchDetails={batchDetails}
            onItemClick={onItemClick}
            batchGoalUID={goalUID}
            isStoreSyllabus={isStoreSyllabus}
            isStoreRoute={isStoreRoute}
          />
        </>
      ) : null}
      {isRecordedOrHybrid ? (
        <RecordedSchedule
          batch={batchDetails}
          isStoreRoute={isStoreRoute}
          accessEndDate={accessEndDate}
          isStoreSyllabus={isStoreSyllabus}
          isEnrolled={isEnrolledInBatch}
        />
      ) : (
        schedule?.length !== 0 && (
          <Week
            weekTitle="Schedule"
            weekInfo=""
            forPlus
            lessonList={schedule}
            isBatch
            batch={batchDetails}
            setAnchorEl={setPDF}
            selectedLesson={selectedLesson}
            onItemClick={onItemClick}
            accessEndDate={accessEndDate}
            isSubscribed={isSubscribed || isBatchSubscribed}
            goalUID={goal?.uid}
          />
        )
      )}
      {(isEnrolledInBatch || isStoreSyllabus) && topicsCovered?.length ? (
        <TopicsCovered
          topicsCovered={topicsCovered}
          goal={batchDetails.goal}
          batch={batchDetails}
          titleTag="h5"
          isStoreRoute={isStoreRoute}
          isStoreSyllabus={isStoreSyllabus}
          storePlaylistFeed={storePlaylistFeed}
        />
      ) : null}
      <SyllabusTest storePlaylistTestFeed={storePlaylistTestFeed} />
      <TrialClasses
        batchUID={batchUID}
        trialClasses={trialClasses}
        goalUID={goalUID}
      />
      <BatchEducators
        educators={authors}
        educatorSectionRef={educatorSectionRef}
      />
      {isEnrolledInBatch && (
        <BatchAbout
          batch={batchDetails}
          batchUID={batchUID}
          isPurchased={isPurchased}
          isRecordedOrHybrid={isRecordedOrHybrid}
          accessEndDate={accessEndDate}
          isDurationBased={isDurationBased}
          displayDuration={displayDuration}
        />
      )}
      {!isEnrolledInBatch && !isStoreSyllabus && topicsCovered?.length ? (
        <TopicsCovered
          topicsCovered={topicsCovered}
          goal={batchDetails.goal}
          batch={batchDetails}
          titleTag="h5"
        />
      ) : null}
      {!isPartnerEducatorBatch && <BatchFeedbacks data={batchFeedback} />}
      {!isPurchased && <BatchFaqs faqs={faqs} />}
      {!isStoreRoute && <SimilarBatches batchUID={batchUID} />}
    </>
  );
};

export default Batch;

Batch.propTypes = {
  batchUID: PropTypes.string.isRequired,
  faqs: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
};

Batch.defaultProps = {
  faqs: []
};
